import { camelCase, cloneDeep, startCase } from 'lodash-es';

export const getSafeError = (error) => error.message;

export const mergeFilters = (tempFilters) => {
  const filters = {};
  tempFilters.forEach((filter) => {
    if (filter.value.length) {
      filters[filter.id] = filter.value.map((value) => value.id);
    }
  });
  return filters;
};

export const combineOtherAndUnknownCategories = (categories) => {
  const other = (categories || []).find((category) => category.source_category === 'other');
  const unknown = (categories || []).find((category) => category.source_category === 'unknown');

  if (other && unknown) {
    const combinedSources = other.sources.concat(unknown.sources);

    return categories.map((category) => {
      if (category.source_category === 'other') {
        return { ...category, sources: combinedSources };
      }

      if (category.source_category === 'unknown') {
        return null;
      }

      return category;
    }).filter(Boolean);
  }
  return categories;
};

export const combineDeclinedAndUnknown = (data, isTableGenderData) => {
  const newData = cloneDeep(data); // Prevents mutation of the store data
  const { declined, unknown } = newData;

  delete newData.declined; // eslint-disable-line no-param-reassign
  delete newData.unknown; // eslint-disable-line no-param-reassign

  const undefinedDataset = {
    review: (declined?.review || 0) + (unknown?.review || 0),
    screen: (declined?.screen || 0) + (unknown?.screen || 0),
    assessment: (declined?.assessment || 0) + (unknown?.assessment || 0),
    offer: (declined?.offer || 0) + (unknown?.offer || 0),
    hired: (declined?.hired || 0) + (unknown?.hired || 0),
  };

  const undefinedDatasetKey = isTableGenderData ? 'genderUndefined' : 'raceUndefined';

  const unsortedData = {
    ...newData,
    [undefinedDatasetKey]: undefinedDataset,
  };
  const unsortedKeys = Object.keys(unsortedData);

  // sort by review count highest to lowest
  const sortedKeys = unsortedKeys.sort((a, b) => {
    const aCount = unsortedData[a].review || 0;
    const bCount = unsortedData[b].review || 0;

    return bCount - aCount;
  });

  const sortedData = {};
  sortedKeys.forEach((key) => {
    sortedData[key] = unsortedData[key];
  });

  return sortedData;
};

export const getPassthroughRate = (currentStageKey, nextStageKey, data) => {
  const rate = data[currentStageKey] > 0
    ? (data[nextStageKey] / data[currentStageKey]) * 100
    : 0;

  return rate.toFixed(1);
};

export const getRowsForCategory = (categoryData, totalLabel = 'Total') => {
  const { sources = [] } = categoryData;
  const rows = sources.map((category) => {
    const {
      ats_source_label: label, review, screen, assessment, offer, hired,
    } = category;

    return {
      label: startCase(label),
      id: camelCase(label),
      review: {
        value: review,
        percent: getPassthroughRate('review', 'screen', category),
      },
      screen: {
        value: screen,
        percent: getPassthroughRate('screen', 'assessment', category),
      },
      assessment: {
        value: assessment,
        percent: getPassthroughRate('assessment', 'offer', category),
      },
      offer: {
        value: offer,
        percent: getPassthroughRate('offer', 'hired', category),
      },
      hired: {
        value: hired,
      },
    };
  });

  const rowsWithTotals = rows.reduce((acc, row) => {
    acc.review += row.review.value || 0;
    acc.screen += row.screen.value || 0;
    acc.assessment += row.assessment.value || 0;
    acc.offer += row.offer.value || 0;
    acc.hired += row.hired.value || 0;
    return acc;
  }, {
    review: 0,
    screen: 0,
    assessment: 0,
    offer: 0,
    hired: 0,
  });

  rows.unshift({
    label: totalLabel,
    id: camelCase(totalLabel),
    review: {
      value: rowsWithTotals.review,
      percent: getPassthroughRate('review', 'screen', rowsWithTotals),
    },
    screen: {
      value: rowsWithTotals.screen,
      percent: getPassthroughRate('screen', 'assessment', rowsWithTotals),
    },
    assessment: {
      value: rowsWithTotals.assessment,
      percent: getPassthroughRate('assessment', 'offer', rowsWithTotals),
    },
    offer: {
      value: rowsWithTotals.offer,
      percent: getPassthroughRate('offer', 'hired', rowsWithTotals),
    },
    hired: {
      value: rowsWithTotals.hired,
    },
  });

  return rows.sort((a, b) => b.review.value - a.review.value);
};
